/**
 * Script resolution (Deliverable A + C) — `scriptOf(locale)` of §6.5.
 *
 * Maps a BCP-47 locale to the writing system the L4 seam needs to pick a font stack
 * (`fontStack(scriptOf(locale))`) and set a correct `lang`. It is **script-subtag aware**:
 * an explicit ISO-15924 script subtag wins over the language default, so `zh-Hant` and
 * `zh-Hans` both resolve to `han`, `sr-Latn` resolves to `latin` while `sr-Cyrl` (and bare
 * `sr`) resolve to `cyrillic`, and `mn-Mong` resolves to `mongolian` even though bare `mn`
 * is Cyrillic.
 *
 * Resolution order (mirrors `resolveCatalog`'s exact → script → primary → fallback shape):
 *   1. an explicit script subtag (`xx-Yyyy`) → its script;
 *   2. otherwise the primary language subtag's default script;
 *   3. otherwise `'latin'` (the never-empty fallback — `fontStackFor` covers it).
 *
 * This is a pure, presentation-only mapping: it factors through the locale string and
 * touches no verdict. Adding a language's script default is a one-line table entry here,
 * and giving a script its font is a one-line entry in `font-stacks.ts` — both zero-core.
 */
import { primarySubtag } from './bidi.js';
/**
 * ISO-15924 script subtag (lower-cased) → our `Script` token. When a locale carries an
 * explicit script (e.g. `zh-Hant`, `az-Arab`, `sr-Latn`) it is authoritative.
 */
const SCRIPT_SUBTAG = {
    latn: 'latin',
    arab: 'arabic',
    // N'Ko and Thaana are their own RTL scripts but share the Arabic-class shaping pipeline;
    // map any with a dedicated stack to it, else to the closest covered stack.
    aran: 'arabic', // Nastaliq variant of Arabic
    deva: 'devanagari',
    beng: 'bengali',
    taml: 'tamil',
    telu: 'telugu',
    hebr: 'hebrew',
    thai: 'thai',
    hang: 'hangul',
    kore: 'hangul',
    hani: 'han',
    hans: 'han',
    hant: 'han',
    jpan: 'han',
    hira: 'han',
    kana: 'han',
    cyrl: 'cyrillic',
    grek: 'greek',
    armn: 'armenian',
    ethi: 'ethiopic',
    nkoo: 'nko',
    thaa: 'thaana',
    mong: 'mongolian',
};
/**
 * Primary language subtag (lower-cased) → its default `Script`. Only languages whose
 * default differs from `latin` need an entry; everything unlisted falls back to `latin`,
 * which always has a font stack. Extend freely — it is a pure table.
 */
const LANGUAGE_SCRIPT = {
    // Arabic-script languages (incl. the RTL set we ship)
    ar: 'arabic',
    fa: 'arabic',
    ur: 'arabic',
    ps: 'arabic',
    ckb: 'arabic', // Sorani Kurdish
    sd: 'arabic', // Sindhi
    ug: 'arabic', // Uyghur
    // Devanagari
    hi: 'devanagari',
    mr: 'devanagari',
    ne: 'devanagari',
    sa: 'devanagari',
    // Other Brahmic
    bn: 'bengali',
    as: 'bengali',
    ta: 'tamil',
    te: 'telugu',
    th: 'thai',
    // Hebrew + Yiddish (Hebrew script)
    he: 'hebrew',
    iw: 'hebrew', // legacy code for Hebrew
    yi: 'hebrew',
    // CJK
    zh: 'han',
    ja: 'han',
    ko: 'hangul',
    // Greek / Cyrillic / Armenian / Ethiopic
    el: 'greek',
    hy: 'armenian',
    // Ethiopic (Geʽez) script — Amharic, Tigrinya (and Tigre, Geʽez itself).
    am: 'ethiopic',
    ti: 'ethiopic',
    gez: 'ethiopic',
    tig: 'ethiopic',
    ru: 'cyrillic',
    uk: 'cyrillic',
    bg: 'cyrillic',
    sr: 'cyrillic',
    mk: 'cyrillic',
    mn: 'cyrillic', // bare `mn` is Cyrillic in modern use; `mn-Mong` overrides
    // Standalone scripts
    dv: 'thaana', // Divehi
    nqo: 'nko', // N'Ko
};
/**
 * Extract the explicit script subtag (ISO-15924, 4 letters) from a BCP-47 locale, if any.
 * `zh-Hant` → `'hant'`, `sr-Latn-RS` → `'latn'`, `en-US` → `undefined` (US is a region).
 */
function scriptSubtagOf(locale) {
    const parts = locale.split('-');
    for (const part of parts.slice(1)) {
        // A script subtag is exactly 4 ASCII letters (regions are 2 letters or 3 digits).
        if (/^[A-Za-z]{4}$/.test(part))
            return part.toLowerCase();
    }
    return undefined;
}
/**
 * The writing system for a locale (Deliverable A + C). Script subtag → language default →
 * `'latin'`. Never throws and never returns an empty value, so the seam always has a usable
 * `fontStack(scriptOf(locale))`.
 */
export function scriptOf(locale) {
    const sub = scriptSubtagOf(locale);
    if (sub && sub in SCRIPT_SUBTAG)
        return SCRIPT_SUBTAG[sub];
    const lang = primarySubtag(locale);
    return LANGUAGE_SCRIPT[lang] ?? 'latin';
}
/** The language subtags with a non-Latin default script (introspection / tests). */
export const NON_LATIN_LANGUAGES = Object.keys(LANGUAGE_SCRIPT);
//# sourceMappingURL=script.js.map